<template>
  <div class="message-text--metadata">
    <span
      class="time"
      :class="{
        'has-status-icon':
          showSentIndicator || showDeliveredIndicator || showReadIndicator,
      }"
    >
      {{ readableTime }}
    </span>
    <span v-if="showReadIndicator" class="read-indicator-wrap">
      <fluent-icon
        v-if="showReadIndicator"
        v-tooltip.top-start="$t('CHAT_LIST.MESSAGE_READ')"
        icon="double-check"
        :class="{
          'action--icon': true,
          'read-tick': true,
          'read-indicator': true,
          'bot-sent': isSentByBot,
        }"
        size="20px"
        viewbox="0 0 20 10"
      />
    </span>
    <span v-else-if="showDeliveredIndicator" class="read-indicator-wrap">
      <fluent-icon
        v-tooltip.top-start="$t('CHAT_LIST.DELIVERED')"
        icon="double-check"
        class="action--icon read-tick"
        size="20px"
        viewbox="0 0 20 10"
      />
    </span>
    <span v-else-if="showSentIndicator" class="read-indicator-wrap">
      <fluent-icon
        v-tooltip.top-start="$t('CHAT_LIST.SENT')"
        icon="check"
        class="action--icon read-tick"
        size="20px"
        viewbox="0 0 20 10"
      />
    </span>
    <fluent-icon
      v-if="isEmail"
      v-tooltip.top-start="$t('CHAT_LIST.RECEIVED_VIA_EMAIL')"
      icon="mail"
      class="action--icon"
      size="16"
    />
    <fluent-icon
      v-if="isPrivate"
      v-tooltip.top-start="$t('CONVERSATION.VISIBLE_TO_AGENTS')"
      icon="lock-closed"
      class="action--icon lock--icon--private"
      size="14"
      type="filled"
      @mouseenter="isHovered = true"
      @mouseleave="isHovered = false"
    />
    <button
      v-if="isATweet && (isIncoming || isOutgoing) && sourceId"
      @click="onTweetReply"
    >
      <fluent-icon
        v-tooltip.top-start="$t('CHAT_LIST.REPLY_TO_TWEET')"
        icon="arrow-reply"
        class="action--icon cursor-pointer"
        size="16"
      />
    </button>
    <a
      v-if="isATweet && (isOutgoing || isIncoming) && linkToTweet"
      :href="linkToTweet"
      target="_blank"
      rel="noopener noreferrer nofollow"
    >
      <fluent-icon
        v-tooltip.top-start="$t('CHAT_LIST.VIEW_TWEET_IN_TWITTER')"
        icon="open"
        class="action--icon cursor-pointer"
        size="16"
      />
    </a>
  </div>
</template>

<script>
import { MESSAGE_TYPE, MESSAGE_STATUS } from 'shared/constants/messages';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import inboxMixin from 'shared/mixins/inboxMixin';
import { mapGetters } from 'vuex';
import timeMixin from '../../../../mixins/time';

export default {
  mixins: [inboxMixin, timeMixin],
  props: {
    sender: {
      type: Object,
      default: () => ({}),
    },
    createdAt: {
      type: Number,
      default: 0,
    },
    storySender: {
      type: String,
      default: '',
    },
    storyId: {
      type: String,
      default: '',
    },
    isEmail: {
      type: Boolean,
      default: true,
    },
    isPrivate: {
      type: Boolean,
      default: true,
    },
    isATweet: {
      type: Boolean,
      default: true,
    },
    hasInstagramStory: {
      type: Boolean,
      default: true,
    },
    messageType: {
      type: Number,
      default: 1,
    },
    messageStatus: {
      type: String,
      default: '',
    },
    sourceId: {
      type: String,
      default: '',
    },
    id: {
      type: [String, Number],
      default: '',
    },
    inboxId: {
      type: [String, Number],
      default: 0,
    },
  },
  computed: {
    ...mapGetters({ currentChat: 'getSelectedChat' }),
    inbox() {
      return this.$store.getters['inboxes/getInbox'](this.inboxId);
    },
    isIncoming() {
      return MESSAGE_TYPE.INCOMING === this.messageType;
    },
    isOutgoing() {
      return MESSAGE_TYPE.OUTGOING === this.messageType;
    },
    isTemplate() {
      return MESSAGE_TYPE.TEMPLATE === this.messageType;
    },
    isDelivered() {
      return MESSAGE_STATUS.DELIVERED === this.messageStatus;
    },
    isRead() {
      return MESSAGE_STATUS.READ === this.messageStatus;
    },
    isSent() {
      return MESSAGE_STATUS.SENT === this.messageStatus;
    },
    /**
     * Determina si el mensaje ha sido enviado por un bot.
     * @returns {boolean} Devuelve true si el mensaje ha sido enviado por un bot, de lo contrario, devuelve false.
     */
    isSentByBot() {
      return !this.sender.type || this.sender.type === 'agent_bot';
    },
    // Una propiedad calculada que devuelve la hora en un formato legible.
    readableTime() {
      return this.messageStamp(
        this.createdAt,
        'LLL d, h:mm a',
        this.$root.$i18n.locale
      );
    },
    screenName() {
      const { additional_attributes: additionalAttributes = {} } =
        this.sender || {};
      return additionalAttributes?.screen_name || '';
    },
    linkToTweet() {
      if (!this.sourceId || !this.inbox.name) {
        return '';
      }
      const { screenName, sourceId } = this;
      return `https://twitter.com/${screenName ||
        this.inbox.name}/status/${sourceId}`;
    },
    linkToStory() {
      if (!this.storyId || !this.storySender) {
        return '';
      }
      const { storySender, storyId } = this;
      return `https://www.instagram.com/stories/${storySender}/${storyId}`;
    },
    showStatusIndicators() {
      if ((this.isOutgoing || this.isTemplate) && !this.isPrivate) {
        return true;
      }
      return false;
    },
    showSentIndicator() {
      if (!this.showStatusIndicators) {
        return false;
      }

      if (this.isAnEmailChannel) {
        return !!this.sourceId;
      }

      if (this.isAWhatsAppChannel || this.isAnEvolutionApiWhatsAppChannel) {
        return this.sourceId && this.isSent;
      }
      return false;
    },
    showDeliveredIndicator() {
      if (!this.showStatusIndicators) {
        return false;
      }

      if (this.isAWhatsAppChannel || this.isAnEvolutionApiWhatsAppChannel) {
        return this.sourceId && this.isDelivered;
      }

      return false;
    },
    showReadIndicator() {
      if (!this.showStatusIndicators) {
        return false;
      }

      if (this.isAWebWidgetInbox) {
        const { contact_last_seen_at: contactLastSeenAt } = this.currentChat;
        return contactLastSeenAt >= this.createdAt;
      }

      if (this.isAWhatsAppChannel || this.isAnEvolutionApiWhatsAppChannel) {
        return this.sourceId && this.isRead;
      }

      return false;
    },
  },
  methods: {
    onTweetReply() {
      bus.$emit(BUS_EVENTS.SET_TWEET_REPLY, this.id);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~dashboard/assets/scss/woot';

.right {
  .message-text--metadata {
    align-items: center;
    .time {
      line-height: var(--space-normal-plus);
      font-weight: var(--font-weight-normal);
      color: var(--conversa2-neutral-900-color);
      margin-right: auto;
    }

    .action--icon {
      color: var(--white);
      opacity: 0.4;

      &.read-tick {
        color: var(--conversa2-neutral-800-color);
      }

      &.read-indicator {
        opacity: 1;
        color: var(--conversa2-blue-400-color);
      }
    }

    .lock--icon--private {
      color: var(--conversa2-yellow-900-color);
      display: block;
      opacity: unset !important;
    }
  }
}

.left {
  .message-text--metadata {
    .time {
      color: var(--conversa2-neutral-400-color);
    }
  }
}

.message-text--metadata {
  align-items: flex-start;
  display: flex;
  position: absolute;
  bottom: var(--space-small);
  left: var(--space-normal);
  right: var(--space-medium);
  .time {
    margin-right: var(--space-small);
    display: block;
    font-size: var(--font-size-micro);
    line-height: var(--space-normal-plus);
    white-space: nowrap;
  }

  .action--icon {
    color: var(--s-900);
  }

  a {
    color: var(--s-900);
  }
}

.activity-wrap {
  .message-text--metadata {
    .time {
      color: var(--s-300);
      display: flex;
      text-align: center;
      font-size: var(--font-size-micro);
      margin-left: 0;
    }
  }
}

.is-image,
.is-video {
  .message-text--metadata {
    bottom: var(--space-normal-plus);
    left: var(--space-medium);
    .action--icon {
      color: var(--white);
      opacity: 0.4;

      &.read-tick {
        color: white;
        opacity: 1;
      }

      &.read-indicator {
        opacity: 1;
        color: var(--conversa2-blue-400-color);
      }
    }
    .time {
      color: var(--white);
      white-space: nowrap;

      &.has-status-icon {
        right: var(--space-large);
        line-height: 2;
      }
    }
    .read-tick {
      bottom: var(--space-small);
      right: var(--space-small);
    }
  }
}

.is-image.is-text,
.is-video.is-text {
  .message-text--metadata {
    bottom: var(--space-small);
    left: var(--space-normal);
    .time {
      color: var(--conversa2-neutral-100-color);
    }
  }
}

li.left {
  .is-image.is-text,
  .is-video.is-text {
    .message-text--metadata .time {
      color: var(--conversa2-neutral-400-color);
    }
  }
}

.is-private {
  .message-text--metadata {
    align-items: center;

    .time {
      color: var(--conversa2-yellow-900-color);
      margin-right: var(--space-small);
      line-height: normal;
    }

    .icon {
      color: var(--s-400);
    }

    .lock--icon--private {
      display: block;
    }
  }
}

.is-private.is-image,
.is-private.is-video {
  .message-text--metadata {
    .time {
      color: var(--white);
    }
    .lock--icon--private {
      color: var(--white);
    }
  }
}

.is-private.is-image.is-text,
.is-private.is-video.is-text {
  .message-text--metadata {
    .time {
      color: var(--conversa2-yellow-900-color) !important;
    }
    .lock--icon--private {
      color: var(--conversa2-yellow-900-color);
    }
  }
}

.is-private.is-text {
  .message-text--metadata {
    .time {
      color: var(--conversa2-yellow-900-color);
    }
    .lock--icon--private {
      display: block;
    }
  }
}

.delivered-icon {
  margin-left: -var(--space-normal);
}

.read-indicator-wrap {
  line-height: 1;
  display: flex;
  align-items: center;
}
</style>
